import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import { MdaExtractMappingDetail } from "./types";

export type MappingFormValues = Partial<MdaExtractMappingDetail>;

export interface MappingFormState {
  values: MappingFormValues;
  errors: Partial<Record<keyof MappingFormValues, string>>;
}

export type FormFieldValue = string | number | boolean | null | undefined;

export interface ValidationResult {
  issues: { message: string; path: (keyof MappingFormValues)[] }[];
}

export const validateMapping = (
  values: MappingFormValues,
): ValidationResult => {
  let issues: ValidationResult["issues"] = [];

  if (!values.sourceColumn?.trim()) {
    issues = [
      ...issues,
      { message: "Source column is required", path: ["sourceColumn"] },
    ];
  }

  return { issues };
};

export interface MappingFormProps {
  formState: MappingFormState;
  sourceColumns: string[];
  onFieldChange: (name: keyof MappingFormValues, value: FormFieldValue) => void;
  onSubmit: () => void;
}

const MappingForm = (props: MappingFormProps) => {
  const { formState, sourceColumns, onFieldChange, onSubmit } = props;
  const formValues = formState.values;
  const formErrors = formState.errors;

  const handleTextFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      onFieldChange(
        name as keyof MappingFormValues,
        value === "" ? null : value,
      );
    },
    [onFieldChange],
  );

  const handleCheckboxFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onFieldChange(event.target.name as keyof MappingFormValues, checked);
    },
    [onFieldChange],
  );

  const handleSourceColumnChange = React.useCallback(
    (event: React.SyntheticEvent, value: string | null) => {
      onFieldChange("sourceColumn", value ?? "");
    },
    [onFieldChange],
  );

  return (
    <Box
      component="form"
      aria-label="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      noValidate
      autoComplete="off"
      sx={{ width: "100%" }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ width: "100%", pt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
            <Autocomplete
              freeSolo
              fullWidth
              options={sourceColumns}
              value={formValues.sourceColumn ?? ""}
              onChange={handleSourceColumnChange}
              onInputChange={handleSourceColumnChange}
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  name="sourceColumn"
                  label="Source Column"
                  error={!!formErrors.sourceColumn}
                  helperText={formErrors.sourceColumn ?? " "}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
            <TextField
              value={formValues.destinationColumn ?? ""}
              onChange={handleTextFieldChange}
              name="destinationColumn"
              label="Destination Column"
              helperText=" "
              fullWidth
            />
          </Grid>
          <Grid
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <FormControl>
              <FormControlLabel
                name="enabled"
                control={
                  <Checkbox
                    checked={formValues.enabled ?? true}
                    onChange={handleCheckboxFieldChange}
                  />
                }
                label="Enabled"
              />
            </FormControl>
          </Grid>
        </Grid>
      </FormGroup>
    </Box>
  );
};

export default MappingForm;
